import assign from 'object-assign';
import {
    getAnalyticsCurrency,
    getLineTotals,
    normalizeSearchTerm,
    toAnalyticsInteger,
    toAnalyticsNumber
} from '@upi/data-layer-utils';
import {
    CommerceItemLists,
    getCanonicalItemListByName,
    isCanonicalItemList,
    isSearchType,
    isSuggestionType
} from '@upi/data-layer-constants';

export const GACustomEvent = {
    FORM_SUBMIT: 'ajax_form_submit',
    SEARCH_RESULTS: 'view_search_results',
    SEARCH_SUGGESTION_CLICK: 'search_suggestion_click',
    IMPRESSION: 'view_item_list',
    ADD_TO_CART: 'add_to_cart',
    REMOVE_FROM_CART: 'remove_from_cart',
    PRODUCT_VIEW: 'view_item',
    PRODUCT_CLICK: 'select_item',
    PROMOTION_CLICK: 'select_promotion',
    PROMOTION_VIEW: 'view_promotion',
    VIEW_CART: 'view_cart',
    BEGIN_CHECKOUT: 'begin_checkout',
    ADD_SHIPPING_INFO: 'add_shipping_info',
    ADD_PAYMENT_INFO: 'add_payment_info',
    USER_LOGIN: 'login',
    REGISTRATION_PROGRESS: 'registration_progress',
    PURCHASE: 'purchase'
};

export const GACommands = {
    CONFIG: 'config',
    GET: 'get',
    SET: 'set',
    EVENT: 'event',
    CONSENT: 'consent'
};

function firstDefined ( ...values ) {
    return values.find( value => value !== undefined && value !== null );
}

function resolveItemList ( item = {}, itemList ) {
    if ( isCanonicalItemList( itemList ) ) {
        return itemList;
    }

    if ( isCanonicalItemList( item.itemList ) ) {
        return item.itemList;
    }

    const itemListFromFields = {
        id: item.itemListId || item.item_list_id,
        name: item.itemListName || item.item_list_name
    };

    if ( isCanonicalItemList( itemListFromFields ) ) {
        return itemListFromFields;
    }

    return getCanonicalItemListByName( item.actionField );
}

const googleAnalyticsProto = {
    sendEvent ( eventName, data ) {
        this.setDataLayer( {
            [GACommands.EVENT]: eventName,
            ...data
        } );

        console.log( '[dataLayer.GA4] send event', eventName, data );
        return this;
    },

    sendEcommerceEvent ( eventName, data, eventData = {} ) {
        this.clearEcommerceData();
        this.setDataLayer( {
            [GACommands.EVENT]: eventName,
            ...eventData,
            ecommerce: {...data}
        } );

        console.log( '[dataLayer.GA4] send Ecommerce event', eventName, data );
        return this;
    },

    setDataLayer ( data ) {
        if ( !window.dataLayer ) {
            window.dataLayer = [];
        }

        window.dataLayer.push( data );

        return this;
    },

    createProductData ( item, itemList ) {
        if ( !item ) {
            return null;
        }

        const resolvedItemList = resolveItemList( item, itemList );
        const price = toAnalyticsNumber( firstDefined( item.linePrice, item.customerPrice, item.catalogPrice, item.price ) );
        const quantity = toAnalyticsInteger( item.quantity, 1 );
        const index = toAnalyticsInteger( firstDefined( item.position, item.index ) );
        const productData = {
            item_id: item.sku || item.itemId || item.item_id,
            quantity: quantity > 0 ? quantity : 1
        };

        const itemName = item.kitName || item.itemName || item.item_name || item.title;
        const itemBrand = item.brandName || item.itemBrand || item.item_brand || item.productName;
        const itemCategory = item.productFamily || item.itemCategory || item.item_category;

        if ( itemName ) {
            productData.item_name = itemName;
        }

        if ( itemBrand ) {
            productData.item_brand = itemBrand;
        }

        if ( itemCategory ) {
            productData.item_category = itemCategory;
        }

        if ( price !== null && price >= 0 ) {
            productData.price = price;
        }

        if ( index !== null && index > 0 ) {
            productData.index = index;
        }

        if ( resolvedItemList ) {
            productData.item_list_id = resolvedItemList.id;
            productData.item_list_name = resolvedItemList.name;
        }

        const itemVariant = item.itemVariant || item.item_variant || item.variant;
        if ( itemVariant ) {
            productData.item_variant = itemVariant;
        }

        for ( let categoryIndex = 2; categoryIndex <= 5; categoryIndex++ ) {
            const categoryValue = item[`itemCategory${categoryIndex}`] || item[`item_category${categoryIndex}`];
            if ( categoryValue ) {
                productData[`item_category${categoryIndex}`] = categoryValue;
            }
        }

        return productData;
    },

    createPromotionData ( {
                              index = 1,
                              promoId = '',
                              promoName = '',
                              promoCreativeName = '',
                              promoPosition = ''
                          } ) {
        return {
            promotion_id: promoId,
            promotion_name: promoName,
            creative_name: promoCreativeName,
            creative_slot: promoPosition,
            index
        }
    },

    clearEcommerceData () {
        console.log( '[dataLayer.GA4] clear Ecommerce Data' );
        this.setDataLayer( {ecommerce: null} );
    }
};

export const googleAnalyticsLayer = assign( {}, googleAnalyticsProto, {
    searchResults ( {searchTerm, searchResultsCount, searchType, searchCategory} ) {
        if ( !isSearchType( searchType ) ) {
            console.warn( `[dataLayer.GA4] Ignoring search event with invalid search type "${searchType}"` );
            return this;
        }

        const data = {
            search_term: normalizeSearchTerm( searchTerm ),
            search_results_count: toAnalyticsInteger( searchResultsCount, 0 ),
            search_type: searchType
        };

        if ( searchCategory ) {
            data.search_category = searchCategory;
        }

        return this.sendEvent( GACustomEvent.SEARCH_RESULTS, data );
    },

    searchSuggestionClick ( {
                                searchTerm,
                                suggestionText,
                                suggestionType,
                                suggestionPosition
                            } ) {
        if ( !isSuggestionType( suggestionType ) ) {
            console.warn(
                `[dataLayer.GA4] Ignoring suggestion event with invalid suggestion type "${suggestionType}"`
            );
            return this;
        }

        return this.sendEvent( GACustomEvent.SEARCH_SUGGESTION_CLICK, {
            search_term: normalizeSearchTerm( searchTerm ),
            suggestion_text: normalizeSearchTerm( suggestionText ),
            suggestion_type: suggestionType,
            suggestion_position: toAnalyticsInteger( suggestionPosition, 1 )
        } );
    },

    productListView ( items = [], itemList ) {
        if ( !isCanonicalItemList( itemList ) ) {
            console.warn( '[dataLayer.GA4] Ignoring product list event without canonical list context', itemList );
            return this;
        }

        const products = items
            .map( item => this.createProductData( item, itemList ) )
            .filter( item => item != null && item.item_id != null );

        if ( products.length === 0 ) {
            return this;
        }

        return this.sendEcommerceEvent( GACustomEvent.IMPRESSION, {
            item_list_id: itemList.id,
            item_list_name: itemList.name,
            items: products
        } );
    },

    crossSellItems ( items = [] ) {
        return this.productListView( items, CommerceItemLists.RELATED_PRODUCTS );
    },

    itemsAdded ( items = [] ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }

        const products = items
            .map( item => this.createProductData( item ) )
            .filter( item => item != null && item.item_list_id && item.item_list_name );

        if ( products.length === 0 ) {
            console.warn( '[dataLayer.GA4] Ignoring add_to_cart without canonical list context' );
            return this;
        }

        return this.sendEcommerceEvent( GACustomEvent.ADD_TO_CART, {
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },

    itemsRemoved ( items = [] ) {
        if ( !Array.isArray( items ) ) {
            items = [items];
        }
        const products = items.map( item => this.createProductData( item ) ).filter( item => item != null );

        this.sendEcommerceEvent( GACustomEvent.REMOVE_FROM_CART, {
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },

    productView ( item ) {
        const itemData = this.createProductData( item );

        this.sendEcommerceEvent( GACustomEvent.PRODUCT_VIEW, {
            currency: getAnalyticsCurrency( item ),
            value: itemData.price,
            items: [itemData]
        } );
    },

    productClick ( item, {itemList, searchTerm} = {} ) {
        const resolvedItemList = resolveItemList( item, itemList );
        if ( !resolvedItemList ) {
            console.warn( '[dataLayer.GA4] Ignoring product click without canonical list context', item );
            return this;
        }

        const itemData = this.createProductData( item, resolvedItemList );
        const eventData = searchTerm === undefined ? {} : {search_term: normalizeSearchTerm( searchTerm )};

        return this.sendEcommerceEvent( GACustomEvent.PRODUCT_CLICK, {
            item_list_id: resolvedItemList.id,
            item_list_name: resolvedItemList.name,
            items: [itemData]
        }, eventData );
    },

    promoView ( promo ) {
        const promotion = this.createPromotionData( promo );

        this.sendEcommerceEvent( GACustomEvent.PROMOTION_VIEW, {
            ...promotion,
            items: [{
                item_id: promo.promoId,
                item_name: promo.promoName
            }]
        } );
    },

    promoClick ( promo ) {
        const promotion = this.createPromotionData( promo );

        this.sendEcommerceEvent( GACustomEvent.PROMOTION_CLICK, {
            ...promotion,
            items: [{
                item_id: promo.promoId,
                item_name: promo.promoName
            }]
        } );
    },

    viewCart ( items ) {
        const products = items.map( item => this.createProductData( item ) ).filter( item => item != null );

        this.sendEcommerceEvent( GACustomEvent.VIEW_CART, {
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },

    beginCheckout ( items ) {
        const products = items.map( item => this.createProductData( item ) ).filter( item => item != null );

        this.sendEcommerceEvent( GACustomEvent.BEGIN_CHECKOUT, {
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },

    purchaseComplete ( {items, orderNumber, promoCode, subtotal, shippingTotal, tax, currency} ) {
        this.sendEcommerceEvent( GACustomEvent.PURCHASE, {
            currency: getAnalyticsCurrency( items, currency ),
            transaction_id: orderNumber,
            value: toAnalyticsNumber( subtotal, 0 ),
            coupon: promoCode,
            shipping: toAnalyticsNumber( shippingTotal, 0 ),
            tax: toAnalyticsNumber( tax, 0 ),
            items: items.map( item => this.createProductData( item ) ).filter( item => item != null )
        } );
    },

    addPaymentInfo ( {paymentMethodType, items} ) {
        const products = items.map( item => this.createProductData( item ) ).filter( item => item != null );

        this.sendEcommerceEvent( GACustomEvent.ADD_PAYMENT_INFO, {
            payment_type: paymentMethodType,
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },
    addShippingInfo ( {shippingMethod, items} ) {
        const products = items.map( item => this.createProductData( item ) ).filter( item => item != null );

        this.sendEcommerceEvent( GACustomEvent.ADD_SHIPPING_INFO, {
            shipping_tier: shippingMethod,
            currency: getAnalyticsCurrency( items ),
            value: getLineTotals( products ),
            items: products
        } );
    },

    formSubmit ( {formName} ) {
        this.sendEvent( GACustomEvent.FORM_SUBMIT, {
            form_name: formName
        } );
    },

    /**
     *
     * @param userId
     * @param signInMethod
     *
     * Common sign in methods would include:
     * "Google": Indicates login through Google OAuth or Google Sign-In.
     * "Facebook": Indicates login through Facebook's authentication system.
     * "Twitter": Indicates login through Twitter authentication.
     * "Email": Indicates a standard email and password login method.
     * "Apple": Indicates login through Apple's Sign in with Apple feature.
     * "Microsoft": Indicates login through Microsoft's account system.
     * "LinkedIn": Indicates login through LinkedIn's authentication system.
     * "GitHub": Indicates login through GitHub's OAuth authentication.
     * "Phone": Indicates login through a phone number and OTP (One Time Password).
     * "Anonymous": Indicates an anonymous login method, where users do not provide identifiable information.
     * "Custom": Indicates a custom login method specific to the application, which does not fall under the common categories.
     */
    userLogin ( {signInMethod} ) {
        this.sendEvent( GACustomEvent.USER_LOGIN, {
            method: signInMethod
        } );
    },

    registrationProgress ( {step, viewCount, result} ) {
        this.sendEvent( GACustomEvent.REGISTRATION_PROGRESS, {
            step: step,
            view_count: viewCount,
            result: result
        } );
    }
} );
